import React, { useState } from 'react';
import type { User } from '../types';
import { getTranslation } from '../utils/translations';
import { createGuidedClient } from '../services/guidedClientService';
import { isNutritionConsultant } from '../utils/userType';
import CopyButton from './CopyButton';
import XIcon from './icons/XIcon';

interface CreateGuidedClientModalProps {
    isOpen: boolean;
    onClose: () => void;
    currentUser: User;
    consultants: User[];
    onCreated?: () => void;
}

const CreateGuidedClientModal: React.FC<CreateGuidedClientModalProps> = ({
    isOpen,
    onClose,
    currentUser,
    consultants,
    onCreated,
}) => {
    const t = (key: Parameters<typeof getTranslation>[1]) =>
        getTranslation(currentUser.language || 'en', key);

    const [email, setEmail] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [consultantId, setConsultantId] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [accessUrl, setAccessUrl] = useState('');

    const nutritionConsultants = consultants.filter(isNutritionConsultant);

    if (!isOpen) return null;

    const reset = () => {
        setEmail('');
        setFirstName('');
        setLastName('');
        setConsultantId('');
        setError('');
        setAccessUrl('');
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const result = await createGuidedClient({
                email: email.trim(),
                firstName: firstName.trim(),
                lastName: lastName.trim() || undefined,
                consultantId,
            });
            setAccessUrl(result.accessUrl);
            onCreated?.();
        } catch (err) {
            setError(err instanceof Error ? err.message : t('errorSaving'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-[200] flex items-center justify-center p-4">
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-lg border border-slate-200 dark:border-slate-700">
                <div className="flex items-center justify-between p-5 border-b border-slate-100 dark:border-slate-800">
                    <h2 className="text-lg font-black text-slate-800 dark:text-slate-100">{t('createGuidedClient')}</h2>
                    <button type="button" onClick={handleClose} className="p-2 text-slate-400 hover:text-slate-600">
                        <XIcon className="w-5 h-5" />
                    </button>
                </div>

                {accessUrl ? (
                    <div className="p-6 space-y-4">
                        <p className="text-sm text-emerald-700 dark:text-emerald-300 font-bold">{t('guidedClientCreated')}</p>
                        <div className="flex items-center gap-2 p-3 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                            <span className="text-xs font-mono text-slate-600 dark:text-slate-300 break-all flex-1 dir-ltr text-start">
                                {accessUrl}
                            </span>
                            <CopyButton text={accessUrl} copyLabel={t('copyLink')} copiedLabel={t('copied')} />
                        </div>
                        <p className="text-xs text-slate-500">{t('guidedClientLinkHint')}</p>
                        <button
                            type="button"
                            onClick={handleClose}
                            className="w-full py-3 bg-brand-sage text-white font-black rounded-xl"
                        >
                            {t('close')}
                        </button>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="p-6 space-y-4">
                        <div>
                            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1.5">{t('email')}</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                className="w-full p-2.5 border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800 dir-ltr"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1.5">{t('firstName')}</label>
                                <input
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                    required
                                    className="w-full p-2.5 border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1.5">{t('lastName')}</label>
                                <input
                                    value={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                    className="w-full p-2.5 border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1.5">{t('userTypeNutritionConsultant')}</label>
                            <select
                                value={consultantId}
                                onChange={(e) => setConsultantId(e.target.value)}
                                required
                                className="w-full p-2.5 border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800"
                            >
                                <option value="">{t('selectConsultant')}</option>
                                {nutritionConsultants.map((c) => (
                                    <option key={c.uid} value={c.uid}>
                                        {c.nickname || c.firstName || c.email}
                                    </option>
                                ))}
                            </select>
                        </div>
                        {error && <p className="text-sm text-red-600">{error}</p>}
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full py-3 bg-brand-sage text-white font-black rounded-xl disabled:opacity-50"
                        >
                            {loading ? t('loading') : t('createGuidedClient')}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
};

export default CreateGuidedClientModal;
